import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import DonateShareCodeInput from '@/components/DonateShareCodeInput';
import { sponsorsEnabled } from '@/lib/feature-flags';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface ShareTargetData {
  id?: string;
  public_name?: string;
  is_confirmed?: boolean;
}

interface ShareTarget {
  type: 'classroom' | 'locker_room';
  data: ShareTargetData;
}

interface ImpactInfo {
  title: string;
  bullets: string[];
  note: string;
}

type DestinationId = 'classrooms' | 'locker_room' | 'smb' | 'rental' | 'project';

const DESTS: { id: DestinationId; label: string; desc: string }[] = [
  { id: 'classrooms', label: 'Hey, Educator!', desc: 'Classroom supplies & teacher banks' },
  { id: 'locker_room', label: 'Locker Rooms', desc: 'Athlete support, travel & essentials' },
  { id: 'smb', label: 'Small Business Fund', desc: 'Micro-grants for community businesses' },
  { id: 'rental', label: 'Rental Assistance', desc: 'Emergency housing stability' },
  { id: 'project', label: 'Project Tower (General)', desc: 'General mission & operations' },
];

function dollarsToCents(v: string | number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

const IMPACT: Record<DestinationId, ImpactInfo> = {
  classrooms: {
    title: 'Your gift powers Hey, Educator!',
    bullets: [
      'Helps teachers buy essential supplies through secure shopping sessions.',
      'Classroom milestone: $800 funds a classroom bank.',
      'When the meter hits $800, Project Tower matches to launch two new classrooms.',
    ],
    note: 'Funds restricted to the Hey, Educator! program.',
  },
  locker_room: {
    title: 'Your gift fuels locker rooms',
    bullets: [
      'Supports travel, dues, gear, and nutrition for young athletes.',
      'Locker room milestone: $1,200 launches a full roster with $100/month per athlete.',
      'Coaches manage budgets and allocate resources to their athletes.',
    ],
    note: 'Funds restricted to the Locker Room program.',
  },
  smb: {
    title: 'Your gift supports small businesses',
    bullets: [
      'Micro-grants for community businesses to grow and sustain.',
      'Priority given to businesses that serve underrepresented communities.',
      'Coming soon: tracked impact dashboards for donors.',
    ],
    note: 'Funds restricted to the Small Business program.',
  },
  rental: {
    title: 'Your gift provides housing stability',
    bullets: [
      'Emergency rental assistance for families facing displacement.',
      'Bridges the gap during job transitions or unexpected hardships.',
      'Administered through verified community partners.',
    ],
    note: 'Funds restricted to the Rental Assistance program.',
  },
  project: {
    title: 'Help Project Tower run & grow',
    bullets: [
      'Outreach, community events, and program enablement.',
      'Operational capacity and platform development.',
      'We balance impact across priority programs as needs shift.',
    ],
    note: 'Unrestricted general fund.',
  },
};

interface MeterData {
  kind: string;
  current_cents: number;
  target_cents: number;
  pct: number;
  cycles_completed: number;
  lifetime_cents: number;
}

function MeterBar({ label, sublabel, current, target, color }: {
  label: string; sublabel: string; current: number; target: number; color: string;
}) {
  const pct = Math.min((current / target) * 100, 100);
  const currentDollars = (current / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const targetDollars = (target / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-cocoa">{label}</span>
        <span className="text-xs text-cocoa/60">{currentDollars} / {targetDollars}</span>
      </div>
      <div className="h-3 rounded-full bg-sand/50 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <p className="text-[10px] text-cocoa/50 mt-0.5">{sublabel}</p>
    </div>
  );
}

export default function Donate() {
  const [dest, setDest] = useState<DestinationId>('classrooms');
  const [amount, setAmount] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [meters, setMeters] = useState<MeterData[]>([]);
  const [shareStatus, setShareStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [shareMessage, setShareMessage] = useState<string>('');
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [manualClassroomId, setManualClassroomId] = useState<string>('');

  // deep-link support
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sp = new URLSearchParams(window.location.search);
    const d = sp.get('dest') as DestinationId | null;
    const a = sp.get('amount');
    if (d && DESTS.some((x) => x.id === d)) setDest(d);
    if (a) setAmount(a);

    const share = (sp.get('share') || '').trim().toUpperCase();
    if (!share) return undefined;

    let cancelled = false;
    setShareStatus('loading');
    setShareMessage('Resolving share code…');

    const resolveShare = async () => {
      const resolveClassroom = async () => {
        const response = await fetch(`${CORE_API}/api/v1/community/share-resolve?code=${encodeURIComponent(share)}`);
        if (!response.ok) return null;
        const raw = await response.json();
        return { kind: 'classroom', payload: raw.data ?? raw };
      };

      const resolveLocker = async () => {
        const response = await fetch(`${CORE_API}/api/v1/locker-room/donate-link?code=${encodeURIComponent(share)}`);
        if (!response.ok) return null;
        const raw = await response.json();
        return { kind: 'locker_room', payload: raw?.data || raw };
      };

      try {
        const classroomResolution = await resolveClassroom();
        if (classroomResolution && !cancelled) {
          handleShareCodeResolved(classroomResolution.payload, classroomResolution.kind);
          return;
        }

        const lockerResolution = await resolveLocker();
        if (lockerResolution && !cancelled) {
          handleShareCodeResolved(lockerResolution.payload, lockerResolution.kind);
          return;
        }

        if (!cancelled) {
          setShareStatus('error');
          setShareMessage('Unable to resolve share code.');
        }
      } catch (err) {
        if (!cancelled) {
          setShareStatus('error');
          setShareMessage((err as Error)?.message || 'Unable to resolve share code.');
        }
      }
    };

    resolveShare();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch funding meters
  useEffect(() => {
    fetch(`${CORE_API}/api/v1/draw/meters`)
      .then((r) => r.json())
      .then((json) => {
        if (json.data?.meters) setMeters(json.data.meters);
      })
      .catch(() => {});
  }, []);

  const getMeter = useCallback((kind: string) => {
    return meters.find((m) => m.kind === kind) || { kind, current_cents: 0, target_cents: 80000 };
  }, [meters]);

  const amountCents = useMemo(() => dollarsToCents(amount), [amount]);
  const valid = amountCents >= 100; // $1 minimum
  const presets = useMemo(() => [25, 50, 100, 250, 800], []);

  async function startCheckout() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      const targetMatchesDestination =
        shareTarget &&
        ((shareTarget.type === 'classroom' && dest === 'classrooms') ||
          (shareTarget.type === 'locker_room' && dest === 'locker_room'));

      const classroomTargetId =
        targetMatchesDestination && shareTarget.type === 'classroom'
          ? shareTarget.data?.id
          : undefined;

      const lockerRoomTargetId =
        targetMatchesDestination && shareTarget.type === 'locker_room'
          ? shareTarget.data?.id
          : undefined;

      const res = await fetch(`${CORE_API}/api/v1/checkout/donate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: amountCents,
          destination: dest, // classrooms | prospects | project | locker_room
          classroom_id: dest === 'classrooms' ? classroomTargetId : undefined,
          locker_room_id: dest === 'locker_room' ? lockerRoomTargetId : undefined,
          success_url: `${window.location.origin}/donate/success`,
          cancel_url: `${window.location.origin}/donate/cancel`,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || 'Unable to start checkout.');
      const raw = await res.json();
      const url = raw?.data?.url ?? raw?.url;
      if (!url) throw new Error('No checkout URL returned.');
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message || 'Something went wrong.');
      setBusy(false);
    }
  }

  const impact = IMPACT[dest];

  const activeShareTarget =
    shareTarget &&
    ((shareTarget.type === 'locker_room' && dest === 'locker_room') ||
      (shareTarget.type === 'classroom' && dest === 'classrooms'))
      ? shareTarget
      : null;

  const handleShareCodeResolved = (data: Record<string, unknown> | null, kind: string) => {
    if (!data) return;

    if (kind === 'locker_room') {
      const locker = (data?.locker_room || {}) as ShareTargetData;
      setShareTarget({ type: 'locker_room', data: { id: locker.id, public_name: locker.public_name } });
      setDest('locker_room');
      setShareStatus('success');
      setShareMessage(`Ready to support ${locker.public_name || 'this locker room'}.`);
      setManualClassroomId('');
      return;
    }

    const classroomData = data as ShareTargetData;
    setShareTarget({ type: 'classroom', data: classroomData });
    setDest('classrooms');
    setShareStatus('success');
    setShareMessage(
      classroomData.public_name
        ? `Ready to donate to ${classroomData.public_name}. ${
            classroomData.is_confirmed ? 'Confirmed classroom.' : 'Awaiting teacher confirmation.'
          }`
        : 'Share code resolved.'
    );
    setManualClassroomId('');
  };

  const applyManualClassroom = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = manualClassroomId.trim();
    if (!code) {
      setShareTarget(null);
      setShareMessage('');
      setShareStatus('idle');
      return;
    }

    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidPattern.test(code)) {
      setShareStatus('error');
      setShareMessage('Please enter a valid classroom UUID.');
      return;
    }

    setShareTarget({ type: 'classroom', data: { id: code, public_name: '', is_confirmed: false } });
    setShareStatus('success');
    setShareMessage('Classroom ID applied. We will route your gift directly.');
    setDest('classrooms');
  };

  function clearClassroomTarget() {
    setShareTarget(null);
    setShareStatus('idle');
    setShareMessage('');
    setManualClassroomId('');
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold text-cocoa">Donate</h1>
        <p className="text-cocoa/80">
          Choose where your gift goes and set any amount. Thank you for helping us
          <span className="font-semibold"> Develop the Sovereign Mind</span>.
        </p>
      </header>

      {sponsorsEnabled() ? (
        <section className="flex flex-col gap-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 shadow-soft sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-cocoa">Donate to Hey, Educator!</h2>
            <p className="text-sm text-cocoa/70">
              Fuel the classroom sponsorship pool. $800 unlocks a full classroom bank.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              className="rounded-xl border border-brick bg-brick px-4 py-2 font-semibold text-white shadow-soft transition hover:bg-brickDark"
              onClick={() => {
                setDest('classrooms');
                setAmount('800');
              }}
            >
              Set to $800 milestone
            </button>
            <button
              type="button"
              className="rounded-xl border border-sand bg-white px-4 py-2 text-cocoa shadow-soft transition hover:bg-shell"
              onClick={() => setDest('classrooms')}
            >
              Focus on Classrooms
            </button>
          </div>
        </section>
      ) : null}

      {/* Destination chooser */}
      <section className="rounded-2xl border border-sand bg-white/80 p-4 shadow-soft">
        <h2 className="mb-3 text-lg font-semibold text-cocoa">Where would you like to donate?</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {DESTS.map(d => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDest(d.id)}
              className={`rounded-xl border p-3 text-left transition ${
                dest === d.id ? 'border-brick bg-brick/10' : 'border-sand bg-white hover:bg-shell/60'
              }`}
            >
              <div className="font-semibold text-cocoa">{d.label}</div>
              <div className="mt-1 text-xs text-cocoa/70">{d.desc}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Program funding meters */}
      <section className="rounded-2xl border border-sand bg-white/80 p-4 shadow-soft">
        <h2 className="mb-3 text-lg font-semibold text-cocoa">Program Progress</h2>
        <div className="space-y-3">
          <MeterBar label="Hey, Educator!" sublabel="$800 launches 2 classrooms" current={getMeter('classroom').current_cents} target={getMeter('classroom').target_cents} color="#B45309" />
          <MeterBar label="Locker Rooms" sublabel="$1,200 launches 1 locker room" current={getMeter('lockerroom').current_cents} target={getMeter('lockerroom').target_cents} color="#0369A1" />
          <MeterBar label="Small Business Fund" sublabel="Coming soon" current={getMeter('smb').current_cents} target={getMeter('smb').target_cents} color="#047857" />
          <MeterBar label="Rental Assistance" sublabel="Coming soon" current={getMeter('rental').current_cents} target={getMeter('rental').target_cents} color="#7C3AED" />
        </div>
        <p className="mt-3 text-[10px] text-cocoa/50">
          Meters fill as donations come in. When a meter reaches its target, a draw ceremony is scheduled.
        </p>
      </section>

      {sponsorsEnabled() ? (
        <section className="rounded-2xl border border-sand bg-white/80 p-4 shadow-soft">
          <h2 className="text-lg font-semibold text-cocoa">Direct Classroom Donation</h2>
          <p className="text-sm text-cocoa/70">Have a share code from a teacher? Resolve it here.</p>
          <div className="mt-3 space-y-4">
            <DonateShareCodeInput onResolved={handleShareCodeResolved} />
            <form className="space-y-2" onSubmit={applyManualClassroom}>
              <label className="block text-sm font-medium text-cocoa/80" htmlFor="donate-classroom-id">
                Or enter a classroom UUID
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  id="donate-classroom-id"
                  type="text"
                  value={manualClassroomId}
                  onChange={(e) => setManualClassroomId(e.target.value)}
                  className="flex-1 rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-brick"
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                className="rounded-xl bg-cocoa px-4 py-2 font-semibold text-white shadow-soft transition hover:bg-cocoa/90"
              >
                Apply
              </button>
              {activeShareTarget ? (
                <button
                  type="button"
                  onClick={clearClassroomTarget}
                  className="rounded-xl border border-sand bg-white px-4 py-2 text-cocoa shadow-soft transition hover:bg-shell"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </form>
        {shareMessage ? (
          <p
            className={`text-sm ${
              shareStatus === 'error'
                ? 'text-brick'
                : shareStatus === 'success'
                ? 'text-mint'
                : 'text-cocoa/70'
            }`}
          >
            {shareMessage}
          </p>
        ) : null}
        {activeShareTarget ? (
          <div className="rounded-xl border border-mint/40 bg-mint/10 p-3 text-sm text-cocoa">
            Directing donation to{' '}
            <span className="font-semibold">
              {activeShareTarget.type === 'locker_room'
                ? activeShareTarget.data.public_name || 'Locker Room'
                : activeShareTarget.data.public_name || 'Classroom'}
            </span>
            {activeShareTarget.data?.id ? <span className="text-cocoa/70"> ({activeShareTarget.data.id})</span> : null}
          </div>
        ) : null}
          </div>
        </section>
      ) : null}

      {/* Amount */}
      <section className="rounded-2xl border border-sand bg-white/80 p-4 shadow-soft">
        <h2 className="mb-3 text-lg font-semibold text-cocoa">Choose an amount</h2>
        <div className="flex flex-wrap gap-2">
          {presets.map(v => (
            <button
              key={v}
              type="button"
              onClick={() => setAmount(String(v))}
              className="badge bg-shell/70 text-cocoa shadow-soft hover:bg-white"
            >
              ${v}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setAmount('800')}
            className="badge bg-amber-100 text-cocoa shadow-soft hover:bg-amber-50"
            title="Classroom milestone"
          >
            $800 (Classroom)
          </button>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <label className="text-sm text-cocoa/80" htmlFor="donation-amount">Custom amount</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-cocoa/60">$</span>
            <input
              id="donation-amount"
              inputMode="decimal"
              placeholder="Enter amount"
              className="w-40 rounded-xl border border-sand bg-white/80 px-6 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-300"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            />
          </div>
          <span className="text-xs text-cocoa/60">(minimum $1)</span>
        </div>
      </section>

      {/* Impact — dynamic by destination */}
      <section className="rounded-2xl border border-sand bg-white/80 p-4 shadow-soft">
        <h2 className="mb-3 text-lg font-semibold text-cocoa">{impact.title}</h2>
        <ul className="ml-5 list-disc space-y-1 text-sm text-cocoa/85">
          {impact.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
        <p className="mt-2 text-xs text-cocoa/60">{impact.note}</p>
        <div className="mt-3 text-xs text-cocoa/60">
          Default distribution for <em>untargeted</em> gifts: <strong>40%</strong> Hey, Educator!, <strong>25%</strong>{' '}
          Locker Rooms, <strong>15%</strong> Small Business Fund, <strong>10%</strong> Rental Assistance, <strong>10%</strong> Operations.
        </div>
      </section>

      {/* Submit */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!valid || busy}
          onClick={startCheckout}
          className="rounded-xl bg-brick px-5 py-3 font-semibold text-white shadow-soft transition hover:bg-brickDark disabled:opacity-60"
        >
          {busy ? 'Starting checkout…' : `Donate $${(amountCents / 100 || 0).toFixed(2)}`}
        </button>
        {error ? <div className="text-sm text-brick">{error}</div> : null}
      </div>
    </div>
  );
}

